import { useRef, useState } from "react";
import { ChevronDown, ChevronUp, Plus, Trash2, Upload, Loader2, ImageOff, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { adminApi, errMsg, imgUrl, uid } from "@/lib/api";
import { useAdmin } from "./AdminContext";

const inputCls = "mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-[15px] text-slate-800 outline-none transition-shadow focus:border-sky-400 focus:ring-2 focus:ring-sky-100";
const tid = (p) => String(p).replace(/[^a-z0-9]+/gi, "-").toLowerCase();

export const PageHead = ({ title, desc }) => (
  <div className="mb-6">
    <h1 className="font-display text-3xl font-semibold text-blue-900">{title}</h1>
    {desc && <p className="mt-1 text-slate-600">{desc}</p>}
  </div>
);

export const Panel = ({ title, desc, children }) => (
  <div className="mb-6 rounded-[1.75rem] bg-white p-5 shadow-sm ring-1 ring-slate-100 sm:p-6">
    {title && <h2 className="font-display text-xl font-semibold text-blue-900">{title}</h2>}
    {desc && <p className="mt-0.5 text-sm text-slate-500">{desc}</p>}
    <div className={title ? "mt-4 space-y-4" : "space-y-4"}>{children}</div>
  </div>
);

export const Input = ({ label, value, onChange, multiline, rows = 4, hint, placeholder, type = "text", testId }) => (
  <label className="block">
    <span className="text-sm font-semibold text-slate-700">{label}</span>
    {multiline ? (
      <textarea data-testid={testId} rows={rows} value={value ?? ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className={`${inputCls} resize-y leading-relaxed`} />
    ) : (
      <input data-testid={testId} type={type} value={value ?? ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className={inputCls} />
    )}
    {hint && <span className="mt-1 block text-xs text-slate-500">{hint}</span>}
  </label>
);

export const Field = ({ path, ...rest }) => {
  const { val, update } = useAdmin();
  return <Input {...rest} testId={`field-${tid(path)}`} value={val(path)} onChange={(v) => update(path, v)} />;
};

export const Toggle = ({ path, label, hint }) => {
  const { val, update } = useAdmin();
  return (
    <div className="flex items-center justify-between gap-4 rounded-xl bg-slate-50 px-4 py-3">
      <div><p className="text-sm font-semibold text-slate-700">{label}</p>{hint && <p className="text-xs text-slate-500">{hint}</p>}</div>
      <Switch data-testid={`toggle-${tid(path)}`} checked={val(path, true) !== false} onCheckedChange={(v) => update(path, v)} />
    </div>
  );
};

export const ImageInput = ({ label, value, onChange, hint, testId }) => {
  const ref = useRef(null);
  const [loading, setLoading] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await adminApi.post("/admin/upload", fd);
      onChange(data.url);
      toast.success("Photo uploaded");
    } catch (err) { toast.error(errMsg(err)); } finally { setLoading(false); }
  };
  return (
    <div>
      <span className="text-sm font-semibold text-slate-700">{label}</span>
      <div className="mt-1.5 flex flex-wrap items-center gap-4">
        <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-slate-100 ring-1 ring-slate-200">
          {value ? <img src={imgUrl(value)} alt="" className="h-full w-full object-cover" /> : <ImageOff className="h-6 w-6 text-slate-400" />}
        </div>
        <div className="flex flex-wrap gap-2">
          <input ref={ref} type="file" accept="image/*" className="hidden" onChange={pick} data-testid={`${testId}-file`} />
          <button type="button" onClick={() => ref.current?.click()} disabled={loading} data-testid={`${testId}-upload-btn`} className="inline-flex items-center gap-2 rounded-full bg-blue-900 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {value ? "Change photo" : "Upload photo"}
          </button>
          {value && <button type="button" onClick={() => onChange("")} data-testid={`${testId}-remove-btn`} className="rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">Remove</button>}
        </div>
      </div>
      {hint && <span className="mt-1 block text-xs text-slate-500">{hint}</span>}
    </div>
  );
};

export const ImageField = ({ path, ...rest }) => {
  const { val, update } = useAdmin();
  return <ImageInput {...rest} testId={`image-${tid(path)}`} value={val(path)} onChange={(v) => update(path, v)} />;
};

export const TagsField = ({ path, label, placeholder }) => {
  const { val, update } = useAdmin();
  const tags = val(path, []) || [];
  const [draft, setDraft] = useState("");
  const add = () => { if (draft.trim()) { update(path, [...tags, draft.trim()]); setDraft(""); } };
  return (
    <div>
      <span className="text-sm font-semibold text-slate-700">{label}</span>
      <div className="mt-2 flex flex-wrap gap-2">
        {tags.map((t, i) => (
          <span key={i} className="inline-flex items-center gap-1.5 rounded-full bg-sky-50 py-1.5 pl-3.5 pr-1.5 text-sm font-medium text-blue-900 ring-1 ring-sky-100">
            {t}
            <button type="button" onClick={() => update(path, tags.filter((_, j) => j !== i))} className="rounded-full p-1 hover:bg-white" aria-label="Remove"><Trash2 className="h-3.5 w-3.5 text-red-500" /></button>
          </span>
        ))}
      </div>
      <div className="mt-2 flex gap-2">
        <input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} placeholder={placeholder} className={`${inputCls} mt-0`} data-testid={`tags-${tid(path)}-input`} />
        <button type="button" onClick={add} className="shrink-0 rounded-xl bg-blue-900 px-4 text-sm font-semibold text-white" data-testid={`tags-${tid(path)}-add`}>Add</button>
      </div>
    </div>
  );
};

const ItemField = ({ f, value, onChange, testId }) => {
  if (f.type === "image") return <ImageInput label={f.label} value={value} onChange={onChange} testId={testId} hint={f.hint} />;
  if (f.type === "select") return (
    <label className="block">
      <span className="text-sm font-semibold text-slate-700">{f.label}</span>
      <select data-testid={testId} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={inputCls}>
        {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
  if (f.type === "switch") return (
    <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
      <span className="text-sm font-semibold text-slate-700">{f.label}</span>
      <Switch data-testid={testId} checked={value !== false} onCheckedChange={onChange} />
    </div>
  );
  return <Input label={f.label} value={value} onChange={onChange} multiline={f.type === "textarea"} rows={f.rows} hint={f.hint} placeholder={f.placeholder} testId={testId} />;
};

export const ListEditor = ({ path, fields, newItem, itemTitle, addLabel = "Add new", allowAdd = true, emptyText = "Nothing added yet." }) => {
  const { val, update } = useAdmin();
  const items = val(path, []) || [];
  const [openId, setOpenId] = useState(null);
  const key = tid(path);
  const setItems = (n) => update(path, n);
  const move = (i, d) => { const n = [...items]; const j = i + d; if (j < 0 || j >= n.length) return; [n[i], n[j]] = [n[j], n[i]]; setItems(n); };
  const add = () => { const it = { id: uid(), ...newItem }; setItems([...items, it]); setOpenId(it.id); };
  const remove = (i) => { if (window.confirm("Delete this item? You can still undo by not saving.")) setItems(items.filter((_, j) => j !== i)); };

  return (
    <div className="space-y-3" data-testid={`list-${key}`}>
      {items.length === 0 && <p className="rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-500">{emptyText}</p>}
      {items.map((it, i) => {
        const id = it.id || String(i);
        const open = openId === id;
        return (
          <div key={id} className="overflow-hidden rounded-2xl ring-1 ring-slate-200" data-testid={`list-${key}-item-${i}`}>
            <div className="flex items-center gap-2 bg-slate-50/80 px-3 py-2.5">
              <GripVertical className="h-4 w-4 shrink-0 text-slate-300" />
              <button type="button" onClick={() => setOpenId(open ? null : id)} className="min-w-0 flex-1 truncate text-left text-[15px] font-semibold text-blue-900" data-testid={`list-${key}-toggle-${i}`}>
                {itemTitle(it) || <span className="font-normal italic text-slate-400">Untitled</span>}
              </button>
              <button type="button" onClick={() => move(i, -1)} className="rounded-lg p-1.5 hover:bg-white" aria-label="Move up"><ChevronUp className="h-4 w-4" /></button>
              <button type="button" onClick={() => move(i, 1)} className="rounded-lg p-1.5 hover:bg-white" aria-label="Move down"><ChevronDown className="h-4 w-4" /></button>
              {allowAdd && <button type="button" onClick={() => remove(i)} className="rounded-lg p-1.5 text-red-500 hover:bg-red-50" aria-label="Delete" data-testid={`list-${key}-delete-${i}`}><Trash2 className="h-4 w-4" /></button>}
            </div>
            {open && (
              <div className="space-y-4 p-4">
                {fields.map((f) => (
                  <ItemField key={f.key} f={f} value={it[f.key]} testId={`list-${key}-${i}-${f.key}`} onChange={(v) => setItems(items.map((x, j) => (j === i ? { ...x, [f.key]: v } : x)))} />
                ))}
              </div>
            )}
          </div>
        );
      })}
      {allowAdd && (
        <button type="button" onClick={add} data-testid={`list-${key}-add-btn`} className="inline-flex items-center gap-2 rounded-full border-2 border-dashed border-sky-300 px-4 py-2 text-sm font-semibold text-blue-900 hover:bg-sky-50">
          <Plus className="h-4 w-4" /> {addLabel}
        </button>
      )}
    </div>
  );
};
